import { Fragment, useCallback, useState, useMemo } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Collapse,
  IconButton,
  Typography,
  Link,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
} from '@mui/material';
import { RouterLink } from 'src/routes/components';
import { useSnackbar } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { fCurrencyFormatKeepZero } from 'src/utils/format-number';
import { fConvertDateFormat } from 'src/utils/format-date';
import {
  AgingBucket,
  ForwardBand,
  IAgingBucketData,
  IAgingClient,
  IAgingInvoice,
  ICreateCollectionActivity,
} from 'src/types';
import { createCollectionActivity } from 'src/api/admin';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import {
  TableHeadCustom,
  TableNameCell,
  type TableHeadCellProps,
  DataTableContainer,
} from 'src/components/table';
import { StatsExportButton } from 'src/components/stats-export';
import AgingBucketBadge from '../aging-bucket-badge';
import { AGING_BUCKET_CONFIG } from '../aging-bucket-config';
import CollectionStatusChip from './collection-status-chip';
import CollectionActivityDialog from './collection-activity-dialog';
import {
  isClientAgingSortField,
  sortClients,
  sortInvoices,
  type ClientAgingSortField,
  type InvoiceSortField,
  type SortOrder,
} from './client-aging-sort';
import {
  anyPaymentTime,
  invoiceNoteKey,
  openAmountOf,
  orderCodesOf,
  paymentTimeLabel,
  priorityOnFloor,
  termsFromInvoices,
  termsLabel,
} from './client-aging-labels';
import { clientAgingWorkbook } from './client-aging-export';

// Sorting — every data column, and what `priority` means — lives in client-aging-sort.ts.

type Props = {
  clients: IAgingClient[];
  activeBucket: AgingBucket | null;
  onActivityLogged: () => void;
  /** The page (and section) the table sits on, in the viewer's language: the export's title. */
  exportTitle: string;
  /** The response's as-of day (yyyy-MM-dd), when it states one: the export's «Situazione al». */
  asOf?: string | null;
  /** NAT-2795 — an admin sees which agent each client belongs to; an agent only has their own. */
  showAgent?: boolean;
  /** Collection status, last contact, notes and the actions menu: admin workflow, not an agent's. */
  allowActions?: boolean;
  /** An agent's own «Da incassare» export: the file says it covers only their clients. */
  ownClientsOnly?: boolean;
  /** The admin's agent filter in words, for the export's preamble. */
  agentLabel?: string | null;
  /** NAT-2801 — the response's «In corso» bands, for the export's «Scadenze future» sheet. */
  forwardBands?: Record<ForwardBand, IAgingBucketData> | null;
};

/**
 * A row key must be unique and must never throw. `invoiceId` is the real identity — the invoice
 * NUMBER is not, because numbering restarts each year and this list spans years, so two rows can
 * share one. The contract also allows a null id and an empty order list, and a backend that has
 * not deployed yet sends neither field, so a naive `orderCodes.join()` would crash the detail
 * view on expand. Index is the last resort: within one client's list it is stable and unique.
 */
const rowKey = (invoice: IAgingInvoice, index: number) =>
  invoice.invoiceId || orderCodesOf(invoice).join('+') || `row-${index}`;

/**
 * `fCurrencyFormat` returns '' for any falsy input, so a genuine 0 renders as an empty cell —
 * indistinguishable from a missing field. That ambiguity is how NAT-2252 stayed hidden. A
 * settled invoice legitimately reads 0,00 here, so zero is formatted explicitly
 * (`fCurrencyFormatKeepZero`) and everything else defers to the shared helper.
 */
const formatOpenAmount = fCurrencyFormatKeepZero;

/**
 * NAT-2805 (rule 58) — the client's overdue total. A real 0 reads bare «0,00» (NAT-2820: it
 * follows its column, whose other cells carry no euro sign), an absent total «—».
 */
const formatOverdueTotal = (amount: number | null | undefined, lang: string) =>
  amount === null || amount === undefined ? '—' : fCurrencyFormatKeepZero(amount, lang);

/**
 * A score of 4 664 and a score of 1.4 are both on this page, so the number is rounded but not
 * abbreviated — the magnitude IS the signal. A client whose score rests on the revenue floor
 * gets a mark: 146 of 255 rows on production are in that state, and for them the number is a
 * rank rather than a measurement.
 */
const formatPriority = (client: IAgingClient) => {
  if (client.priorityScore === undefined) return '';
  const rounded = Math.round(client.priorityScore);
  return priorityOnFloor(client) ? `${rounded}*` : `${rounded}`;
};

/**
 * WhatsApp wants digits only, with a country code and no leading plus.
 *
 * Numbers in this database are typed by hand: "+39 333 1234567", "333/1234567", "0039 …".
 * Anything that does not survive normalisation into something plausibly dialable disables the
 * action rather than opening a chat with a wrong number.
 */
const whatsappNumber = (phone?: string | null): string | null => {
  if (!phone) return null;
  const digits = phone.replace(/[^\d]/g, '').replace(/^00/, '');
  if (digits.length < 8) return null;
  return digits.length <= 10 ? `39${digits}` : digits;
};

/** The columns of the invoices under a client; every one sorts. */
const INVOICE_COLUMNS: {
  field: InvoiceSortField;
  labelKey: string;
  align?: 'right';
}[] = [
  { field: 'orderCode', labelKey: 'codice_ordine' },
  { field: 'invoiceNumber', labelKey: 'numero_fattura' },
  { field: 'amount', labelKey: 'importo', align: 'right' },
  { field: 'invoiceDueDate', labelKey: 'data_scadenza' },
  { field: 'daysOverdue', labelKey: 'giorni_ritardo', align: 'right' },
];

export default function ClientAgingTable({
  clients,
  activeBucket,
  onActivityLogged,
  exportTitle,
  asOf,
  showAgent = false,
  allowActions = true,
  ownClientsOnly = false,
  agentLabel = null,
  forwardBands = null,
}: Props) {
  const { t, currentLang } = useLocales('operativaDashboard');
  const { t: tCommon } = useLocales();
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();

  const [expandedClientId, setExpandedClientId] = useState<number | null>(null);
  const [sortField, setSortField] = useState<ClientAgingSortField>('priority');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [invoiceSort, setInvoiceSort] = useState<{ field: InvoiceSortField; order: SortOrder }>({
    field: 'invoiceDueDate',
    order: 'asc',
  });
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // The whole client, not just the slug: the actions need its id, phone and current status.
  const [menuClient, setMenuClient] = useState<IAgingClient | null>(null);
  const [dialogType, setDialogType] = useState<'note' | 'promise' | null>(null);
  const [dialogClientId, setDialogClientId] = useState<number | null>(null);

  // NAT-2800 — a feed with no payment on any row (absent, or null on every row, as the Operativa
  // receivables feed sends) would give two walls of dashes: the columns appear once a row has one.
  const hasPaymentTime = useMemo(() => anyPaymentTime(clients ?? []), [clients]);
  // A sorted payment column that has gone (no row carries one now) cannot keep ordering the table
  // or naming the file: the default order takes over as real state — reset during render, before
  // anything is committed — so «Priorità» then flips normally and the old sort never comes back.
  if (!hasPaymentTime && (sortField === 'paymentDays' || sortField === 'termsDays')) {
    setSortField('priority');
    setSortOrder('desc');
  }

  const TABLE_HEAD = useMemo(() => {
    const agentCell: TableHeadCellProps[] = showAgent
      ? [{ field: 'agentName', title: t('agente') }]
      : [];
    const collectionCells: TableHeadCellProps[] = allowActions
      ? [
          { field: 'collectionStatus', title: t('stato_incasso') },
          { field: 'lastContactedAt', title: t('ultimo_contatto') },
        ]
      : [];
    const paymentCells: TableHeadCellProps[] = hasPaymentTime
      ? [
          { field: 'paymentDays', title: t('pagamento_medio'), tooltip: t('pagamento_medio_nota') },
          { field: 'termsDays', title: t('termini'), tooltip: t('termini_in_fattura_nota') },
        ]
      : [];
    const actionsCell: TableHeadCellProps[] = allowActions
      ? [{ field: 'actions', title: t('azioni'), align: 'right', disableSort: true }]
      : [];
    const head: TableHeadCellProps[] = [
      { field: 'expand', width: 40, disableSort: true },
      { field: 'priority', title: t('priorita'), align: 'right' },
      { field: 'clientName', title: t('cliente'), kind: 'name' },
      ...agentCell,
      { field: 'totalOverdue', title: t('totale_scaduto'), align: 'right' },
      { field: 'worstBucket', title: t('bucket_peggiore') },
      ...collectionCells,
      { field: 'invoiceCount', title: t('n_fatture'), align: 'right' },
      ...paymentCells,
      ...actionsCell,
    ];
    return head;
  }, [t, showAgent, allowActions, hasPaymentTime]);

  // Same locale mapping `cash-cycle-trend-chart.tsx` and friends use: the app declares only 'en'
  // and 'it', not full BCP-47 tags. `sensitivity: 'base'` ignores case/accents for ordering (but
  // keeps them as a tie-break via `numeric`'s codepoint fallback would not — `Intl.Collator`
  // itself resolves remaining ties); `numeric: true` orders embedded numbers by value.
  const clientNameCollator = useMemo(
    () =>
      new Intl.Collator(currentLang.value === 'en' ? 'en-US' : 'it-IT', {
        sensitivity: 'base',
        numeric: true,
      }),
    [currentLang.value],
  );

  const filteredClients = useMemo(() => {
    if (!clients?.length) return [];
    let result = clients;
    if (activeBucket) {
      result = clients.filter((c) => c.worstBucket === activeBucket);
    }
    // The bucket filter preserves order, so the server's ranking survives it untouched.
    return sortClients(result, sortField, sortOrder, clientNameCollator);
  }, [clients, activeBucket, sortField, sortOrder, clientNameCollator]);

  // NAT-2481 — the export is the table as it stands: the bucket's clients, in the screen's order.
  const buildExport = useCallback(async () => {
    const sortTitle = TABLE_HEAD.find((h) => h.field === sortField)?.title ?? '';
    // An admin's line is the filter; the own-clients line is only an agent's. Neither: no line.
    const ownClientsLabel = ownClientsOnly ? t('export_solo_tuoi_clienti') : null;
    return clientAgingWorkbook({
      // The invoices in the screen's invoice sort too, so the second sheet reads like the table.
      clients: filteredClients.map((c) => ({
        ...c,
        invoices: sortInvoices(
          c.invoices ?? [],
          invoiceSort.field,
          invoiceSort.order,
          clientNameCollator,
        ),
      })),
      page: exportTitle,
      asOf,
      bucketLabel: activeBucket ? t(AGING_BUCKET_CONFIG[activeBucket].labelKey) : null,
      agentLabel: showAgent ? agentLabel : ownClientsLabel,
      includeAgent: showAgent,
      includeCollection: allowActions,
      includePaymentTime: hasPaymentTime,
      forwardBands,
      sortLabel: `${sortTitle} (${t(sortOrder === 'asc' ? 'export_crescente' : 'export_decrescente')})`,
      t: (k, o) => (k === 'export_esportato_il' ? tCommon(k, o) : t(k, o)),
    });
  }, [
    TABLE_HEAD,
    sortField,
    sortOrder,
    hasPaymentTime,
    filteredClients,
    invoiceSort,
    clientNameCollator,
    exportTitle,
    asOf,
    activeBucket,
    showAgent,
    allowActions,
    ownClientsOnly,
    agentLabel,
    forwardBands,
    t,
    tCommon,
  ]);

  const handleSort = (field: string) => {
    // 'priority' is sortable so a user who sorted by a column can get the server's ranking
    // back without reloading the page.
    if (!isClientAgingSortField(field)) return;
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleInvoiceSort = (field: InvoiceSortField) =>
    setInvoiceSort((previous) =>
      previous.field === field
        ? { field, order: previous.order === 'asc' ? 'desc' : 'asc' }
        : { field, order: 'desc' },
    );

  const handleToggleExpand = (clientId: number) => {
    setExpandedClientId(expandedClientId === clientId ? null : clientId);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, client: IAgingClient) => {
    setMenuAnchor(event.currentTarget);
    setMenuClient(client);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuClient(null);
  };

  const logActivity = async (payload: ICreateCollectionActivity) => {
    try {
      await createCollectionActivity(payload);
      // The backend decides the resulting status by a ladder — a client already further along
      // is deliberately not moved back — so the row is refetched rather than patched here.
      onActivityLogged();
    } catch (error) {
      // A silent failure is the worst outcome on this page: the operator believes the call is
      // logged, the row still reads "da contattare", and they phone the customer twice.
      enqueueSnackbar(t('errore_salvataggio'), { variant: 'error' });
      throw error;
    }
  };

  const openDialog = (type: 'note' | 'promise', clientId: number) => {
    setDialogType(type);
    setDialogClientId(clientId);
    handleMenuClose();
  };

  return (
    <Card>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5 }}>
        <StatsExportButton build={buildExport} disabled={!filteredClients.length} />
      </Box>
      <DataTableContainer>
        <Table stickyHeader size="small">
          <TableHeadCustom
            order={sortOrder}
            orderBy={sortField}
            headLabel={TABLE_HEAD}
            // 9 columns, but the first is the expand chevron, not a name: pinning it would pin a marker.
            stickyFirstColumn={false}
            onSort={handleSort}
          />
          <TableBody>
            {filteredClients.map((client) => {
              const isExpanded = expandedClientId === client.clientId;

              return (
                <Fragment key={client.clientId}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleToggleExpand(client.clientId)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        <Iconify
                          icon={
                            isExpanded ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'
                          }
                          width={16}
                        />
                      </IconButton>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                        {formatPriority(client)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {/* A client with no slug on file has no page: its name stays plain
                            text rather than a link to /clients/null. */}
                        {client.clientSlug ? (
                          <Link
                            component={RouterLink}
                            href={paths.admin.clients.details(client.clientSlug)}
                            variant="subtitle2"
                            color="inherit"
                            underline="hover"
                            sx={{ display: 'block' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <TableNameCell>{client.clientName}</TableNameCell>
                          </Link>
                        ) : (
                          <TableNameCell sx={{ fontWeight: 600 }}>
                            {client.clientName}
                          </TableNameCell>
                        )}
                        {client.clientSlug ? (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {client.clientSlug}
                          </Typography>
                        ) : null}
                        {allowActions && client.latestNote ? (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontStyle: 'italic',
                              color: 'text.secondary',
                              maxWidth: 260,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {client.latestNote}
                          </Typography>
                        ) : null}
                      </Box>
                    </TableCell>
                    {showAgent && (
                      <TableCell>
                        <Typography variant="body2">{client.agentName}</Typography>
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                        {formatOverdueTotal(client.totalOverdue, currentLang.value)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <AgingBucketBadge bucket={client.worstBucket} />
                    </TableCell>
                    {allowActions && (
                      <>
                        <TableCell>
                          <CollectionStatusChip status={client.collectionStatus} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {client.lastContactedAt
                              ? fConvertDateFormat(client.lastContactedAt)
                              : t('mai_contattato')}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                    <TableCell align="right">{client.invoiceCount}</TableCell>
                    {hasPaymentTime && (
                      <>
                        <TableCell>
                          <Typography variant="body2">{paymentTimeLabel(client, t)}</Typography>
                        </TableCell>
                        <TableCell>
                          {/* Hover only: the keyboard reads the same note on the header. */}
                          <Tooltip
                            title={termsFromInvoices(client) ? t('termini_in_fattura_nota') : ''}
                            describeChild
                            arrow
                          >
                            <Typography variant="body2">{termsLabel(client, t)}</Typography>
                          </Tooltip>
                        </TableCell>
                      </>
                    )}
                    {allowActions && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, client);
                          }}
                        >
                          <Iconify icon="eva:more-vertical-fill" width={20} />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_HEAD.length}
                      sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, pl: 6, backgroundColor: 'background.neutral' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                {INVOICE_COLUMNS.map(({ field, labelKey, align }) => (
                                  <TableCell
                                    key={field}
                                    align={align}
                                    sx={{ fontWeight: 'fontWeightBold' }}
                                    sortDirection={
                                      invoiceSort.field === field ? invoiceSort.order : false
                                    }
                                  >
                                    <TableSortLabel
                                      active={invoiceSort.field === field}
                                      direction={
                                        invoiceSort.field === field ? invoiceSort.order : 'asc'
                                      }
                                      onClick={() => handleInvoiceSort(field)}
                                    >
                                      {t(labelKey)}
                                    </TableSortLabel>
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortInvoices(
                                client.invoices ?? [],
                                invoiceSort.field,
                                invoiceSort.order,
                                clientNameCollator,
                              ).map((invoice, rowIndex) => (
                                <TableRow key={rowKey(invoice, rowIndex)}>
                                  <TableCell>
                                    {/* One invoice can cover several orders, so this is a list. */}
                                    {orderCodesOf(invoice).map((orderCode) => (
                                      <Link
                                        key={orderCode}
                                        component={RouterLink}
                                        href={paths.admin.orderTypes.clientOrders.details(
                                          orderCode,
                                        )}
                                        variant="body2"
                                        underline="hover"
                                        sx={{
                                          display: 'block',
                                        }}
                                      >
                                        {orderCode}
                                      </Link>
                                    ))}
                                  </TableCell>
                                  <TableCell>
                                    {invoice.invoiceNumber ? (
                                      <Typography variant="body2">
                                        {invoice.invoiceNumber}
                                      </Typography>
                                    ) : null}
                                    {invoiceNoteKey(invoice) ? (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: 'block',
                                          fontStyle: 'italic',
                                          color:
                                            invoice.amountBasis === 'excluded'
                                              ? 'warning.dark'
                                              : 'text.secondary',
                                        }}
                                      >
                                        {t(invoiceNoteKey(invoice) ?? '')}
                                      </Typography>
                                    ) : null}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatOpenAmount(openAmountOf(invoice), currentLang.value)}
                                  </TableCell>
                                  <TableCell>
                                    {fConvertDateFormat(invoice.invoiceDueDate)}
                                  </TableCell>
                                  <TableCell align="right">{invoice.daysOverdue}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </DataTableContainer>

      <Popover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* v9 MenuItems require MenuListContext; a bare Popover does not provide it. */}
        <MenuList>
          <MenuItem
            disabled={!menuClient?.clientSlug}
            onClick={() => {
              if (menuClient?.clientSlug) {
                router.push(paths.admin.clients.details(menuClient.clientSlug));
              }
              handleMenuClose();
            }}
          >
            <Iconify icon="eva:eye-fill" width={20} sx={{ mr: 1 }} />
            {t('vedi_fatture')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuClient) {
                // No status field on purpose. The backend promotes an untouched client to
                // "contattato" and leaves anyone further along where they are; sending the
                // status explicitly would ask it to demote an escalated client instead.
                // Fire and forget: logActivity has already reported any failure to the user.
                logActivity({
                  clientId: menuClient.clientId,
                  activityType: 'contacted',
                }).catch(() => {});
              }
              handleMenuClose();
            }}
          >
            <Iconify icon="eva:checkmark-circle-2-fill" width={20} sx={{ mr: 1 }} />
            {t('segna_contattato')}
          </MenuItem>
          <MenuItem onClick={() => menuClient && openDialog('note', menuClient.clientId)}>
            <Iconify icon="eva:edit-2-fill" width={20} sx={{ mr: 1 }} />
            {t('aggiungi_nota')}
          </MenuItem>
          <MenuItem onClick={() => menuClient && openDialog('promise', menuClient.clientId)}>
            <Iconify icon="eva:calendar-fill" width={20} sx={{ mr: 1 }} />
            {t('registra_promessa')}
          </MenuItem>
          <MenuItem
            disabled={!whatsappNumber(menuClient?.clientPhone)}
            onClick={() => {
              const number = whatsappNumber(menuClient?.clientPhone);
              if (number) window.open(`https://wa.me/${number}`, '_blank', 'noopener,noreferrer');
              handleMenuClose();
            }}
          >
            <Iconify icon="eva:message-circle-fill" width={20} sx={{ mr: 1 }} />
            {t('whatsapp')}
          </MenuItem>
        </MenuList>
      </Popover>

      <CollectionActivityDialog
        open={dialogType !== null}
        clientId={dialogClientId}
        activityType={dialogType}
        onClose={() => setDialogType(null)}
        onSubmit={logActivity}
      />
    </Card>
  );
}
